import { lazy, Suspense, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './amplifyConfig';
import { getUser, logout } from './api/auth';
import { getLicenseStatus } from './api/license';
import { getCurrentOrg } from './api/orgs';
import { setAuthErrorHandler } from './api/client';
import { isAdminRole } from './constants/roles';
import AuthPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import InviteModal from './components/InviteModal';
import ErrorBoundary from './components/ErrorBoundary';
import { useToast } from './components/Toast';
import './App.css';

// Lazy-loaded routes (HS-98): keeps Three.js / ReactFlow / Plotly / KaTeX — all pulled
// in by ProjectPage's viewer/panel tree (and Plotly by DatabasesPage) — out of the
// initial bundle, so the Auth → Dashboard landing path loads light. Each chunk is
// fetched on first navigation to its route, behind the <Suspense> fallback below.
const ProjectPage = lazy(() => import('./pages/ProjectPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const InvitePage = lazy(() => import('./pages/InvitePage'));
const DatabasesPage = lazy(() => import('./pages/DatabasesPage'));
const QualityCheckPage = lazy(() => import('./pages/QualityCheckPage'));
const LearnPage = lazy(() => import('./pages/LearnPage'));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));

export default function App() {
  const toast = useToast();
  const [user, setUser] = useState(undefined);
  const [checking, setChecking] = useState(true);
  const [license, setLicense] = useState(null);
  const [org, setOrg] = useState(null);

  // Global 401 handler: when any API call sees an expired/invalid session, sign
  // out and clear user state → the declarative <Navigate to="/"> redirects to
  // login (instead of background polls failing silently). Debounced in client.js.
  useEffect(() => {
    setAuthErrorHandler(() => {
      logout().catch(() => {});
      setUser(null);
      setLicense(null);
      setOrg(null);
      toast.error('Your session has expired. Please sign in again.');
    });
    return () => setAuthErrorHandler(null);
  }, [toast]);

  // Load license + org together. Both are best-effort: a failure (e.g. a
  // pre-tenancy user before personal-org backfill, or an offline profile) must
  // not break the app — we just render in a degraded state.
  async function loadContext() {
    const [lic, o] = await Promise.all([
      getLicenseStatus().catch((err) => {
        console.error('License check failed:', err);
        return null;
      }),
      getCurrentOrg().catch((err) => {
        console.error('Org load failed:', err);
        return null;
      }),
    ]);
    setLicense(lic);
    setOrg(o);
  }

  useEffect(() => {
    getUser().then(async (u) => {
      setUser(u);
      if (u) await loadContext();
      setChecking(false);
    });
  }, []);

  async function handleLogout() {
    await logout();
    setUser(null);
    setLicense(null);
    setOrg(null);
  }

  async function handleLogin() {
    const u = await getUser();
    setUser(u);
    if (u) await loadContext();
  }

  if (checking) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <p>Loading...</p>
      </div>
    );
  }

  // Tolerant of an offline-signed license (status 'valid', source 'offline_signed').
  const hasValidLicense = license?.hasLicense && license?.status === 'valid';
  const isAdmin = isAdminRole(org?.role);
  const isOwner = org?.role === 'owner';

  // License + shared keys live on the Team & Admin page now, so send unlicensed
  // users there (owners activate; others see status + guidance).
  const projectElement =
    user && hasValidLicense ? (
      <ProjectPage />
    ) : user ? (
      <Navigate to="/admin" />
    ) : (
      <Navigate to="/" />
    );

  // Learn is license-gated on the same terms as the project routes, unlike
  // /databases and /quality-check: every button on it creates a project and
  // navigates straight onto `projectElement`, so an unlicensed visitor would
  // manufacture an orphan project and then be bounced to /admin.
  const learnElement =
    user && hasValidLicense ? (
      <LearnPage onLogout={handleLogout} />
    ) : user ? (
      <Navigate to="/admin" />
    ) : (
      <Navigate to="/" />
    );

  return (
    <BrowserRouter>
      {user && <InviteModal onOrgUpdate={setOrg} />}
      <ErrorBoundary>
        <Suspense
          fallback={
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
              }}
            >
              <p>Loading…</p>
            </div>
          }
        >
          <Routes>
            <Route
              path="/"
              element={user ? <Navigate to="/dashboard" /> : <AuthPage onLogin={handleLogin} />}
            />
            {/* Auth-only, like /projects — it lists projects and talks to the
                assistant, it doesn't open the editor. `hasValidLicense` is passed
                anyway because ONE control on it ("Build it myself") navigates onto
                `projectElement`, and offering that unlicensed would manufacture an
                orphan project and then bounce the user to /admin. */}
            <Route
              path="/dashboard"
              element={
                user ? (
                  <Dashboard onLogout={handleLogout} org={org} hasValidLicense={hasValidLicense} />
                ) : (
                  <Navigate to="/" />
                )
              }
            />
            <Route
              path="/databases"
              element={user ? <DatabasesPage org={org} /> : <Navigate to="/" />}
            />
            <Route
              path="/databases/:dbId"
              element={user ? <DatabasesPage org={org} /> : <Navigate to="/" />}
            />
            <Route
              path="/quality-check"
              element={user ? <QualityCheckPage onLogout={handleLogout} /> : <Navigate to="/" />}
            />
            {/* Auth-only, not license-gated: it lists and deletes projects, it
                doesn't open the editor (see the comment above projectElement). */}
            <Route
              path="/projects"
              element={user ? <ProjectsPage onLogout={handleLogout} /> : <Navigate to="/" />}
            />
            <Route path="/learn" element={learnElement} />
            <Route path="/project/:id" element={projectElement} />
            <Route path="/project/:id/tile/:tileId" element={projectElement} />
            <Route path="/profile" element={user ? <ProfilePage /> : <Navigate to="/" />} />
            <Route path="/settings" element={user ? <SettingsPage /> : <Navigate to="/" />} />
            <Route
              path="/admin"
              element={
                user ? (
                  <AdminPage
                    org={org}
                    onOrgUpdate={setOrg}
                    isAdmin={isAdmin}
                    isOwner={isOwner}
                    license={license}
                    onLicenseUpdate={setLicense}
                  />
                ) : (
                  <Navigate to="/" />
                )
              }
            />
            <Route
              path="/invite/:token"
              element={user ? <InvitePage onOrgUpdate={setOrg} /> : <Navigate to="/" />}
            />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
